import {BindingPhase} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/BindingPhase';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {NamedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {SpreadsheetsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {
    BindingDirection,
    EventBinding,
    IBinding,
    IBindingSnapshot,
    IEventBindingSnapshot,
    IPropBindingSnapshot,
    PropBinding,
    RepeaterFixation
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {PropConfig} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/PropConfig';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {BindingId, SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {BindingFormatBehavior} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface IBindingsSnapshot
{
    [bindingId:string]:IBindingSnapshot;
}

export class BindingState
{
    @observable
    public runtime:BindingRuntimeState = new BindingRuntimeState();

    @observable
    public viewState:BindingViewState = new BindingViewState();

    private constructor()
    {
    }

    public static create():BindingState
    {
        return new BindingState();
    }

    public static toJson:ITransformer<BindingState, IBindingsSnapshot> = createTransformer((bindingState:BindingState) =>
    {
        const result = {} as IBindingsSnapshot;

        for( const [bindingId, binding] of Array.from(bindingState.runtime.bindings.entries()) )
        {
            if( binding )
                result[bindingId] = binding.isPropBinding
                    ? PropBinding.toJson(binding as PropBinding)
                    : EventBinding.toJson(binding as EventBinding);
        }

        return result;
    });

    public static fromJson(json:IBindingsSnapshot,
                           spreadsheets:SpreadsheetsState,
                           surface:SurfaceState,
                           currentState?:BindingState,
                           currentJson?:IBindingsSnapshot):BindingState
    {
        if( !currentState )
            return BindingState.fromJsonDeep(json, spreadsheets, surface);

        if( !currentJson )
            currentJson = BindingState.toJson(currentState);

        const bindingState:BindingState = new BindingState();

        const bindings:Map<BindingId, IBinding> = new Map();

        for( const [bindingId, bindingJson] of Object.entries(json) )
        {
            const currentBindingJson = currentJson[bindingId];
            const currentBindingState = currentState.runtime.bindings.get(bindingId);

            if( bindingJson )
            {
                const binding = bindingJson.isPropBinding
                    ? PropBinding.fromJson(
                        bindingJson as IPropBindingSnapshot,
                        spreadsheets,
                        surface,
                        currentBindingState as PropBinding,
                        currentBindingJson as IPropBindingSnapshot
                    )
                    : EventBinding.fromJson(
                        bindingJson as IEventBindingSnapshot,
                        spreadsheets,
                        surface,
                        currentBindingState as EventBinding,
                        currentBindingJson as IEventBindingSnapshot
                    );

                bindings.set(bindingId, binding);
            }
        }

        bindingState.runtime.bindings = bindings;

        return bindingState;
    }

    public static fromJsonDeep(json:IBindingsSnapshot,
                               spreadsheets:SpreadsheetsState,
                               surface:SurfaceState):BindingState
    {
        const bindingState:BindingState = new BindingState();

        const bindings:Map<BindingId, IBinding> = new Map();

        if( json )
            for( const [bindingId, bindingJson] of Object.entries(json) )
            {
                const binding = bindingJson.isPropBinding
                    ? PropBinding.fromJsonDeep(
                        bindingJson as IPropBindingSnapshot,
                        spreadsheets,
                        surface
                    )
                    : EventBinding.fromJsonDeep(
                        bindingJson as IEventBindingSnapshot,
                        spreadsheets,
                        surface
                    );

                if( binding )
                    bindings.set(bindingId, binding);
                else
                    console.warn('WARNING!!! Invalid Binding has been skipped');
            }

        bindingState.runtime.bindings = bindings;

        return bindingState;
    }
}

export class BindingRuntimeState
{
    @observable
    public bindings:Map<BindingId, IBinding> = new Map();

    @action.bound
    public getBinding(bindingId:BindingId):IBinding
    {
        return this.bindings.get(bindingId);
    }

    @action.bound
    public getPropBinding(bindingId:BindingId):PropBinding
    {
        const binding = this.bindings.get(bindingId);
        return binding instanceof PropBinding && binding || null;
    }

    @action.bound
    public getEventBinding(bindingId:BindingId):EventBinding
    {
        const binding = this.bindings.get(bindingId);
        return binding instanceof EventBinding && binding || null;
    }

    @action.bound
    public addBinding(binding:IBinding):void
    {
        this.bindings.set(binding.id, binding);
    }

    @action.bound
    public removeBinding(bindingId:BindingId):void
    {
        this.bindings.delete(bindingId);
    }
}

export class BindingViewState
{
    @observable public activeSheetId:SheetId = null;

    @observable public bindingPhase:BindingPhase = null;

    @observable public currentProp:Prop<any> = null;
    @observable public currentPath:PropPath = null;

    @observable public currentEventProp:EventProp = null;

    @observable public editSheetId:SheetId = null;
    @observable public editNamedRange:NamedRange = null;
    @observable public editPropBinding:PropBinding = null;
    @observable public editEventBinding:EventBinding = null;

    @observable public selectionStartLocation:MeshLocation = null;
    @observable public selectionEndLocation:MeshLocation = null;

    public clear():void
    {
        this.activeSheetId = null;

        this.bindingPhase = null;

        this.currentProp = null;
        this.currentPath = null;

        this.currentEventProp = null;

        this.editSheetId = null;
        this.editNamedRange = null;
        this.editPropBinding = null;
        this.editEventBinding = null;

        this.selectionStartLocation = null;
        this.selectionEndLocation = null;
    }

    @computed
    public get isEditing():boolean
    {
        return !!this.editSheetId;
    }

    @computed
    public get editBinding():IBinding
    {
        return this.editPropBinding || this.editEventBinding;
    }

    @computed
    public get isArray():boolean
    {
        return this.currentProp != null && this.currentProp.getIsArray(this.currentPath);
    }

    @computed
    public get isComposite():boolean
    {
        return this.currentProp != null && this.currentProp.getIsComposite(this.currentPath);
    }

    @computed
    public get propConfig():PropConfig<any>
    {
        return this.currentProp != null && this.currentProp.getPropertyConfig(this.currentPath);
    }

    @computed
    public get mayFormatBeApplied():boolean
    {
        const {propConfig} = this;
        return propConfig
            && propConfig.spec.binding.formatBehavior !== BindingFormatBehavior.None
            && Type.String.isEqualTo(propConfig.type);
    }

    @computed
    public get renderingSheetId():SheetId
    {
        return this.editSheetId || this.activeSheetId;
    }

    @computed
    public get propLabels():string[]
    {
        const {propConfig, currentEventProp} = this;
        if( currentEventProp )
            return [currentEventProp.name];

        if( propConfig )
        {
            if( propConfig.isArray )
                return propConfig.propertyNames;

            return [propConfig.name];
        }

        return [];
    }

    @computed
    public get repeaterFixation():RepeaterFixation
    {
        const binding = this.editBinding;
        return binding && binding.repeaterFixation;
    }

    @computed
    public get useFormat():boolean
    {
        const {editPropBinding} = this;
        return editPropBinding && editPropBinding.useFormat;
    }

    @computed
    public get normalizedSelectionBounds():{isDown:boolean, startLoc:MeshLocation, endLoc:MeshLocation}
    {
        let {isEditing, currentProp, editPropBinding, propConfig, selectionStartLocation: startLoc, selectionEndLocation: endLoc} = this;

        if( !endLoc )
            endLoc = startLoc;

        // collapse if binding is not for array prop
        if( !propConfig || !propConfig.isArray )
            startLoc = endLoc;

        // if endLoc is less then startLoc...
        if( endLoc.compareTo(startLoc) < 0 )
        {
            if( isEditing )
            {
                // ... when extending existing range - just collapse them
                endLoc = startLoc;
            }
            else
            {
                // ... when creating new - swap locations
                const tmpLoc = startLoc;
                startLoc = endLoc;
                endLoc = tmpLoc;
            }
        }

        const rowCount = endLoc.rowIndex - startLoc.rowIndex + 1;
        const colCount = endLoc.colIndex - startLoc.colIndex + 1;
        const isDown = isEditing
            ? !editPropBinding || editPropBinding.direction === BindingDirection.Down
            : colCount <= rowCount;

        let width = propConfig ? propConfig.width : 1;
        if( isDown )
            endLoc = startLoc.translate(rowCount - 1, width - 1);
        else
            endLoc = startLoc.translate(width - 1, colCount - 1);

        return {isDown, startLoc, endLoc};
    }
}
